import React from 'react';
import Layout from '../components/Layout';

export default function TermsConditions() {
  return (
    <Layout>
      <div className="terms-container">
        <div className="page-header">
          <h1 className="page-title">Terms & Conditions</h1>
          <p className="page-subtitle">Please read carefully before proceeding</p>
        </div>

        <div className="terms-content">
          <div className="terms-section">
            <h2>1. Introduction and Agreement</h2>
            <p>
              Welcome to Syntriq's Internship Program. By accessing this website and enrolling in our internship program, you agree to be bound by these Terms and Conditions, as well as our Privacy Policy. This comprehensive agreement is legally binding and applies to all participants, regardless of their location or employment status. If you do not agree to any of these terms, you must cease use of our services immediately and refrain from enrolling in our programs.
            </p>
            <p>
              Syntriq (hereinafter referred to as "we," "us," "our," or "the Company") is committed to providing high-quality internship experiences and professional development opportunities. However, it is essential that all participants understand and comply with the guidelines set forth in this document. Failure to comply may result in immediate termination of your participation and forfeiture of any benefits.
            </p>
          </div>

          <div className="terms-section">
            <h2>2. Internship Program Overview and Participation</h2>
            <h3>2.1 Program Description</h3>
            <p>
              The Syntriq Internship Program is a structured learning and development initiative designed to provide participants with practical experience in various technology domains including but not limited to Web Development, Mobile App Development, Cloud Computing, Data Science, and Artificial Intelligence. The program is typically 8-12 weeks in duration, though specific timelines may vary based on the selected domain and track.
            </p>
            <p>
              Participants will be assigned projects, mentors, and resources based on their skill level and domain selection. The program includes regular evaluations, feedback sessions, and opportunities for networking with industry professionals. Syntriq maintains the right to modify program structure, duration, or content at its sole discretion.
            </p>

            <h3>2.2 Eligibility Requirements</h3>
            <p>
              To participate in the Syntriq Internship Program, you must meet the following requirements:
            </p>
            <ul>
              <li>Be at least 18 years of age or have parental consent</li>
              <li>Possess a valid email address and contact information</li>
              <li>Have basic knowledge relevant to the selected domain</li>
              <li>Commit to completing the entire program duration</li>
              <li>Maintain regular communication with mentors and the Syntriq team</li>
              <li>Follow all code of conduct guidelines throughout participation</li>
              <li>Not be currently enrolled in conflicting programs or contracts</li>
            </ul>

            <h3>2.3 Commitment and Time Requirements</h3>
            <p>
              Participants are expected to dedicate a minimum of 20-30 hours per week to the internship program. This includes attending scheduled sessions, completing assignments, working on projects, and participating in evaluation activities. Syntriq may terminate your participation if you fail to meet these time commitment requirements consistently.
            </p>
          </div>

          <div className="terms-section">
          <h2>3. User Conduct and Code of Conduct</h2>
            <h3>3.1 Professional Behavior</h3>
            <p>
              All participants must maintain professional and respectful conduct throughout the internship program. This includes interactions with mentors, fellow participants, and Syntriq staff. Syntriq reserves the right to remove any participant who violates this code of conduct.
            </p>

            <h3>3.2 Prohibited Conduct</h3>
            <p>
              The following behaviors are strictly prohibited:
            </p>
            <ul>
              <li>Harassment, bullying, or discrimination of any kind</li>
              <li>Use of offensive, abusive, or vulgar language</li>
              <li>Violation of intellectual property or plagiarism</li>
              <li>Cheating, collusion, or academic dishonesty</li>
              <li>Sharing confidential program materials without authorization</li>
              <li>Attempting to gain unauthorized access to platform systems</li>
              <li>Spamming or sending unsolicited communications</li>
              <li>Any illegal activity or violation of laws</li>
            </ul>

            <h3>3.3 Consequences of Violation</h3>
            <p>
              Violations of the code of conduct may result in:
            </p>
            <ul>
              <li>Immediate termination of program participation</li>
              <li>Forfeiture of any earned credits or benefits</li>
              <li>Certificate denial or revocation</li>
              <li>Legal action and prosecution</li>
              <li>Permanent ban from future Syntriq programs</li>
            </ul>
          </div>
          </div>

          <div className="terms-section">
            <h2>4. Certificate Issuance and Conditions</h2>
            <h3>4.1 Certificate Requirements</h3>
            <p>
              Certificates are issued only after meeting <span className="highlight">ALL</span> of the following mandatory conditions:
            </p>
            <ul>
              <li>Successful completion of 100% of the assigned project work</li>
              <li>Achievement of minimum 70% marks in program evaluations</li>
              <li>Submission of the final project within the stipulated deadline</li>
              <li>Satisfactory quality of work as per program standards</li>
              <li>Completion of all mandatory assignments and assessments</li>
              <li>Successful payment of the program fee</li>
              <li>Verification of identity through email confirmation</li>
              <li>No violations of code of conduct throughout the program</li>
            </ul>

            <h3>4.2 Certificate Authentication and Verification</h3>
            <p>
              Each certificate issued by Syntriq contains a unique Certificate ID and can be verified in real-time on our certificate verification portal. The certificate includes details such as participant name, internship domain, batch information, issuance date, and a unique security code. Employers and educational institutions can verify the authenticity of any certificate through our verification system.
            </p>

            <h3>4.3 Certificate Timeline</h3>
            <p>
              Certificates are typically issued within 5-7 business days after program completion and verification. In some cases, processing may take up to 15 business days. Participants will receive notification via email once their certificate is ready for download.
            </p>

            <h3>4.4 Certificate Download and Retention</h3>
            <p>
              Certificates can be downloaded from your account dashboard for a period of one year from the issuance date. After this period, the certificate will be archived but can still be accessed through our certificate verification system using your Certificate ID. Syntriq is not responsible for loss of certificate files on participant devices.
            </p>
          </div>

          <div className="terms-section">
            <h2>5. Project Submission and Intellectual Property Rights</h2>
            <h3>5.1 Project Ownership</h3>
            <p>
              You retain full ownership and intellectual property rights to your original project work and code. Syntriq does not claim ownership of your creative work or source code. However, by submitting your project to our platform, you grant Syntriq a non-exclusive, royalty-free license to use your project for the following purposes:
            </p>
            <ul>
              <li>Educational and training purposes</li>
              <li>Portfolio showcase with proper attribution</li>
              <li>Case studies and success stories with your permission</li>
              <li>Platform improvement and quality assessment</li>
              <li>Promotional materials highlighting participant success</li>
            </ul>

            <h3>5.2 Project Marketplace Listing</h3>
            <p>
              Approved projects may be featured in our project marketplace for learning purposes. Projects will be displayed with your name and contact information (unless you opt out). This exposure can help build your professional portfolio and attract potential employers or clients.
            </p>

            <h3>5.3 Attribution and Credit</h3>
            <p>
              All projects will include clear attribution to the original creator. You will be credited as the project author, along with your GitHub profile or portfolio link if provided.
            </p>

            <h3>5.4 Third-Party Content and Libraries</h3>
            <p>
              If your project uses third-party libraries, frameworks, or open-source code, you are responsible for ensuring compliance with their respective licenses. Syntriq will not be liable for any licensing violations.
            </p>
          </div>

          <div className="terms-section">
            <h2>6. Certificate Misuse, Fraud, and Revocation</h2>
            <h3>6.1 Prohibited Activities</h3>
            <p>
              The following activities are strictly prohibited and constitute fraud:
            </p>
            <ul>
              <li>Falsifying, altering, or modifying your certificate in any way</li>
              <li>Claiming credit for work or achievements not completed by you</li>
              <li>Sharing your unique Certificate ID for unauthorized verification</li>
              <li>Using the certificate to misrepresent your qualifications</li>
              <li>Selling, transferring, or lending your certificate to others</li>
              <li>Creating fake or counterfeit Syntriq certificates</li>
              <li>Using the certificate for fraudulent employment or educational applications</li>
              <li>Publicly disparaging Syntriq or making false claims about the program</li>
            </ul>

            <h3>6.2 Fraud Detection and Investigation</h3>
            <p>
              Syntriq maintains sophisticated systems to detect fraudulent certificates and misuse. If suspicious activity is detected, we will conduct a thorough investigation. During the investigation period, your certificate may be temporarily suspended from verification.
            </p>

            <h3>6.3 Certificate Revocation Process</h3>
            <p>
              Any confirmed misuse or fraud will result in immediate and permanent revocation of your certificate. Upon revocation:
            </p>
            <ul>
              <li>Your certificate will be marked as "REVOKED" in our system</li>
              <li>Certificate verification will show as invalid and fraudulent</li>
              <li>Your profile will be flagged in our database</li>
              <li>You will lose all access to download your certificate</li>
              <li>Your name may be added to our public list of invalidated certificates</li>
              <li>Future enrollment in our programs will be denied</li>
              <li>Legal action may be pursued to recover damages</li>
            </ul>

            <h3>6.4 Legal Consequences</h3>
            <p>
              Certificate fraud is a serious matter that may violate local and international laws. Syntriq reserves the right to report suspected fraud to law enforcement agencies and legal authorities. Penalties may include:
            </p>
            <ul>
              <li>Civil lawsuits for damages and attorney fees</li>
              <li>Criminal prosecution and imprisonment</li>
              <li>Substantial financial penalties and fines</li>
              <li>Permanent criminal record</li>
              <li>Damage to your professional reputation</li>
            </ul>
          </div>

          <div className="terms-section">
          <h2>7. Contribution and Support for the Program</h2>
            <h3>7.1 Program Contribution</h3>
            <p>
              To support the Syntriq Internship Program and ensure the sustainability of our operations, we request a contribution amount of <span className="highlight">₹99</span> from participants. This contribution is vital to:
            </p>
            <ul>
              <li>Support our dedicated team of experienced mentors and instructors</li>
              <li>Maintain and upgrade technical infrastructure and learning platforms</li>
              <li>Provide quality learning materials, resources, and documentation</li>
              <li>Ensure 24/7 technical support and assistance throughout the program</li>
              <li>Organize webinars, workshops, and networking events</li>
              <li>Maintain secure servers for project hosting and data storage</li>
              <li>Generate and authenticate certificates for program completion</li>
              <li>Develop and improve our platform and user experience</li>
            </ul>
            <p>
              This contribution helps us maintain our commitment to excellence and ensures that future participants continue to receive high-quality education and support. Your contribution directly supports the hard work and dedication of our team.
            </p>

            <h3>7.2 Voluntary Contribution and Non-Refundable Nature</h3>
            <p>
              While we greatly appreciate contributions from participants, please understand that the ₹99 contribution is non-refundable once payment has been successfully processed. This is to ensure we can plan and allocate resources effectively for the program. Before making your contribution, please carefully consider your commitment to the program.
            </p>
            <p>
              The non-refundable policy applies in the following situations:
            </p>
            <ul>
              <li>If you decide to voluntarily withdraw from the program</li>
              <li>If you are unable to complete program requirements</li>
              <li>In case of personal circumstances or emergencies</li>
              <li>Due to changes in your career plans or interests</li>
              <li>If you find the program doesn't meet your expectations</li>
              <li>Due to any technical issues on your end</li>
            </ul>
            <p>
              This policy ensures that funds contributed by participants are used solely for maintaining and improving the program infrastructure and supporting our team.
            </p>

            <h3>7.3 What Your Contribution Supports</h3>
            <p>
              When you make a contribution of ₹99, you're directly supporting:
            </p>
            <ul>
              <li>Salaries and stipends for our mentor team</li>
              <li>Cloud infrastructure and hosting costs</li>
              <li>Certificate generation and verification systems</li>
              <li>Email and communication systems</li>
              <li>Learning platform maintenance</li>
              <li>Regular platform updates and security patches</li>
              <li>Technical support and customer service team</li>
              <li>Industry partnerships and networking opportunities</li>
            </ul>

            <h3>7.4 Payment Processing and Security</h3>
            <p>
              All payments are processed securely through Razorpay, our PCI-DSS compliant payment gateway partner. Your payment information is encrypted and protected using industry-standard SSL protocols. Syntriq does not store or have access to your complete credit card or banking information. You are responsible for maintaining the security of your payment credentials.
            </p>
            <p>
              Payment confirmation will be sent to your registered email address within 24 hours of successful transaction. If you do not receive confirmation, please contact our support team immediately with your transaction reference number.
            </p>

            <h3>7.5 Payment Receipt and Invoice</h3>
            <p>
              An invoice and payment receipt will be generated automatically upon successful contribution. This receipt will include your transaction ID, payment date, and amount paid. Please retain this receipt for your records and future reference. Your contribution is appreciated and helps sustain our program.
            </p>

            <h3>7.6 Taxes and Additional Charges</h3>
            <p>
              The stated contribution amount is inclusive of all applicable taxes in India. No additional charges or hidden fees will be levied. However, participants may incur charges from their banking institutions or payment providers, which are the responsibility of the participant.
            </p>

            <h3>7.7 Payment Disputes and Chargebacks</h3>
            <p>
              If you initiate a chargeback or payment dispute after payment confirmation, your program access will be immediately revoked. Additionally, Syntriq reserves the right to take legal action to recover the disputed amount and associated legal fees. Any fraudulent chargebacks may result in legal prosecution.
            </p>

            <h3>7.8 Late or Missed Contributions</h3>
            <p>
              Participants who are unable to make the contribution before the specified deadline can work with our support team to explore flexible payment options or discuss their situation. We understand that not everyone's financial circumstances are the same, and we're open to conversations about supporting your participation in the program.
            </p>

            <h3>7.9 Multiple Contributions and Duplicate Charges</h3>
            <p>
              If you make multiple contributions by mistake, we appreciate your extra support! The duplicate contributions are non-refundable. However, you will receive additional credits or extended access equivalent to the duplicate contribution amount. Please contact support to activate these benefits.
            </p>

            <h3>7.10 Contribution Refund Exceptions</h3>
            <p>
              While contributions are generally non-refundable, we may consider exceptions in rare circumstances such as:
            </p>
            <ul>
              <li>Technical errors or payment processing failures on our platform</li>
              <li>Duplicate charges due to system errors</li>
              <li>In case of program cancellation by Syntriq</li>
            </ul>
            <p>
              Such cases will be reviewed individually, and decisions will be communicated within 5-7 business days. Please contact our support team with details of your situation.
            </p>

          <div className="terms-section">
            <h2>8. Limitation of Liability and Disclaimers</h2>
            <p>
              Syntriq shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services, certificate, or internship program. This includes but is not limited to:
            </p>
            <ul>
              <li>Loss of business opportunities or income</li>
              <li>Damage to professional reputation</li>
              <li>Mental or emotional distress</li>
              <li>Third-party claims or disputes</li>
              <li>Technical failures or service interruptions</li>
            </ul>
            <p>
              The total liability of Syntriq in any circumstance shall not exceed the amount paid by the participant for the program fee.
            </p>
          </div>

          <div className="terms-section">
            <h2>9. Privacy and Data Protection</h2>
            <p>
              Participant information collected during registration and program participation will be handled in accordance with our Privacy Policy and applicable data protection laws. We may use your information for program administration, certificate issuance, portfolio display (with consent), and communication purposes. Your information will not be sold to third parties without your explicit consent.
            </p>
          </div>

          <div className="terms-section">
            <h2>10. Amendments and Updates</h2>
            <p>
              Syntriq reserves the right to modify these terms and conditions at any time without prior notice. Material changes will be communicated via email to all participants. Continued participation in the program after any amendments constitutes your acceptance of the updated terms. It is your responsibility to review these terms periodically for updates.
            </p>
          </div>

          <div className="terms-section">
            <h2>11. Governing Law and Jurisdiction</h2>
            <p>
              These terms and conditions are governed by and construed in accordance with the laws of India, specifically the Indian Contract Act, 1872, and applicable cyber laws. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in India. Both parties consent to submit to the jurisdiction of Indian courts.
            </p>
          </div>

          <div className="terms-section">
            <h2>12. Contact and Support</h2>
            <p>
              If you have any questions about these Terms and Conditions, need assistance, or wish to report violations, please contact us at:
            </p>
            <ul>
              <li>Email: <a href="mailto:support@syntriq.in" className="highlight" style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0066cc' }}>support@syntriq.in</a></li>
              <li>Website: <a href="https://www.syntriq.in" target="_blank" rel="noopener noreferrer" className="highlight" style={{ textDecoration: 'underline', cursor: 'pointer', color: '#0066cc' }}>www.syntriq.in</a></li>
              <li>Response Time: 24-48 business hours</li>
            </ul>
          </div>

          <div className="last-updated">
            <p><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p><strong>Effective Date:</strong> January 1, 2026</p>
            <p>By clicking "I Agree" or using our services, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions in their entirety.</p>
            <p style={{ marginTop: '1.5rem', fontStyle: 'italic', color: 'var(--text-muted)' }}>
              These terms and conditions constitute the entire agreement between you and Syntriq regarding the internship program and supersede any prior agreements or understandings.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
